import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import NavBar from './components/NavBar';
import BottomNavigation from './components/BottomNavigation';
import Dashboard from './pages/Dashboard';
import FamilyPage from './pages/FamilyPage';
import AuthPage from './pages/AuthPage';
import OnboardUser from './pages/OnboardUser';
import ProfilePage from './pages/ProfilePage';
import FamilyChoice from './pages/FamilyChoice';
import SuperAdmin from './pages/SuperAdmin';
import SuperAdminUserProfile from './pages/SuperAdminUserProfile';
import SuperAdminUserRecordings from './pages/SuperAdminUserRecordings';
import SuperAdminAudioEnhancement from './pages/SuperAdminAudioEnhancement';
import SetupSuperAdmin from './pages/SetupSuperAdmin';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Listen from './pages/Listen';
import Record from './pages/Record';
import Study from './pages/Study';
import Subscription from './pages/Subscription';
import CompletedChapters from './pages/CompletedChapters';
import ColorPalette from './components/ColorPalette';
import SoundWaveDemo from './components/SoundWaveDemo';
import ShareableNoteView from './components/ShareableNoteView';
import AwaitingApproval from './pages/AwaitingApproval';
import PlansPage from './pages/PlansPage';
import ListeningPlansPage from './pages/ListeningPlansPage';
import PassagesPlansPage from './pages/PassagesPlansPage';
import GiftPage from './pages/GiftPage';
import RedeemGift from './pages/RedeemGift';
import Shop from './pages/Shop';
import TutorialPage from './pages/TutorialPage';
import FavoritesPage from './pages/FavoritesPage';
import RemindersPage from './pages/RemindersPage';

// Lazy load components for better performance
const CreateFamily = React.lazy(() => import('./pages/CreateFamily'));
const JoinFamily = React.lazy(() => import('./pages/JoinFamily'));
const AcceptInvitation = React.lazy(() => import('./pages/AcceptInvitation'));
const CreateInvitedAccount = React.lazy(() => import('./pages/CreateInvitedAccount'));

// Redirect to a static (non-React) page
const StaticRedirect = ({ to }) => {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
};

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0d1520]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-white/60">Loading...</p>
    </div>
  </div>
);

// Layout wrapper component for authenticated routes
const AuthenticatedLayout = ({ children }) => {
  return (
    <main>
      {children}
    </main>
  );
};

function AppContent() {
  const { currentUser, loading } = useAuth();

  // While auth state is resolving, show nothing to avoid flashing wrong nav or routes
  if (loading) return <LoadingSpinner />;

  const hasFamilies = currentUser?.familyIds?.length > 0;
  const hasPendingRequest = !hasFamilies && !!currentUser?.pendingFamilyId;
  const authRedirect = currentUser
    ? (hasFamilies ? '/dashboard' : hasPendingRequest ? '/awaiting-approval' : `/onboard?intent=${currentUser.signupIntent || 'create'}`)
    : null;

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="min-h-screen bg-white dark:bg-[#0d1520]">
        {/* Desktop NavBar - hidden on mobile, only show when logged in */}
        {currentUser && (
          <div className="hidden lg:block sticky top-0 z-[10000]">
            <NavBar />
          </div>
        )}

        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={
              currentUser && hasFamilies
                ? <Navigate to="/dashboard" replace />
                : <StaticRedirect to="/home.html" />
            } />
            <Route path="/home" element={<StaticRedirect to="/home.html" />} />
            <Route path="/about" element={<StaticRedirect to="/home.html" />} />
            {/* Redirect logged-in users away from auth page */}
            <Route path="/auth" element={authRedirect ? <Navigate to={authRedirect} replace /> : <AuthPage />} />
            <Route path="/onboard" element={<OnboardUser />} />
            <Route path="/awaiting-approval" element={<AwaitingApproval />} />
            <Route path="/colors" element={<ColorPalette />} />
            <Route path="/soundwave" element={<SoundWaveDemo />} />
            <Route path="/notes/:userId/:noteId" element={<ShareableNoteView />} />
            <Route path="/gift" element={<GiftPage />} />
            <Route path="/redeem-gift" element={<RedeemGift />} />
            
            {/* Authenticated routes with layout */}
            <Route path="/dashboard" element={
              <AuthenticatedLayout>
                <Dashboard />
              </AuthenticatedLayout>
            } />
            <Route path="/listen" element={
              <AuthenticatedLayout>
                <Listen />
              </AuthenticatedLayout>
            } />
            <Route path="/record" element={
              <AuthenticatedLayout>
                <Record />
              </AuthenticatedLayout>
            } />
            <Route path="/family" element={
              <AuthenticatedLayout>
                <FamilyPage />
              </AuthenticatedLayout>
            } />
            <Route path="/completed-chapters" element={
              <AuthenticatedLayout>
                <CompletedChapters />
              </AuthenticatedLayout>
            } />
            <Route path="/study" element={
              <AuthenticatedLayout>
                <Study />
              </AuthenticatedLayout>
            } />
            <Route path="/profile" element={
              <AuthenticatedLayout>
                <ProfilePage />
              </AuthenticatedLayout>
            } />
            <Route path="/subscription" element={
              <AuthenticatedLayout>
                <Subscription />
              </AuthenticatedLayout>
            } />
            <Route path="/plans" element={
              <AuthenticatedLayout>
                <PlansPage />
              </AuthenticatedLayout>
            } />
            <Route path="/listening-plans" element={
              <AuthenticatedLayout>
                <ListeningPlansPage />
              </AuthenticatedLayout>
            } />
            <Route path="/passages-plans" element={
              <AuthenticatedLayout>
                <PassagesPlansPage />
              </AuthenticatedLayout>
            } />
            <Route path="/favorites" element={
              <AuthenticatedLayout>
                <FavoritesPage />
              </AuthenticatedLayout>
            } />
            <Route path="/reminders" element={
              <AuthenticatedLayout>
                <RemindersPage />
              </AuthenticatedLayout>
            } />
            <Route path="/super-admin" element={
              <AuthenticatedLayout>
                <SuperAdmin />
              </AuthenticatedLayout>
            } />
            <Route path="/super-admin/users/:userId" element={
              <AuthenticatedLayout>
                <SuperAdminUserProfile />
              </AuthenticatedLayout>
            } />
            <Route path="/super-admin/users/:userId/recordings" element={
              <AuthenticatedLayout>
                <SuperAdminUserRecordings />
              </AuthenticatedLayout>
            } />
            <Route path="/super-admin/users/:userId/audio-enhancement" element={
              <AuthenticatedLayout>
                <SuperAdminAudioEnhancement />
              </AuthenticatedLayout>
            } />
            <Route path="/setup-super-admin" element={
              <AuthenticatedLayout>
                <SetupSuperAdmin />
              </AuthenticatedLayout>
            } />
            <Route path="/family-choice" element={
              <AuthenticatedLayout>
                <FamilyChoice />
              </AuthenticatedLayout>
            } />
            <Route path="/shop" element={
              <AuthenticatedLayout>
                <Shop />
              </AuthenticatedLayout>
            } />
            <Route path="/tutorial" element={
              <AuthenticatedLayout>
                <TutorialPage />
              </AuthenticatedLayout>
            } />
            
            {/* Lazy loaded routes */}
            <Route path="/create-family" element={
              <AuthenticatedLayout>
                <CreateFamily />
              </AuthenticatedLayout>
            } />
            <Route path="/join-family" element={
              <AuthenticatedLayout>
                <JoinFamily />
              </AuthenticatedLayout>
            } />
            <Route path="/accept-invitation" element={
              <AuthenticatedLayout>
                <AcceptInvitation />
              </AuthenticatedLayout>
            } />
            <Route path="/create-invited-account" element={
              <AuthenticatedLayout>
                <CreateInvitedAccount />
              </AuthenticatedLayout>
            } />
            
            {/* 404 fallback */}
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0d1520]">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">404</h1>
                  <p className="text-gray-600 dark:text-white/60 mb-8">Page not found</p>
                  <a href="/" className="btn-primary">Go Home</a>
                </div>
              </div>
            } />
          </Routes>
        </Suspense>
        
        {/* Mobile Bottom Navigation - Only show when user is logged in */}
        {currentUser && <BottomNavigation />}
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <TranslationProvider>
          <AppContent />
        </TranslationProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App; 